import { useSelectedArea } from 'state/canvas/selected-area';
import useCanvas from 'components/canvas/hooks/use-canvas';
import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import pointInPolygon from 'point-in-polygon';
import { SurfaceNodeType, SurfaceNodeUnion } from 'graphql/types';
import { Dimension, Position } from 'types';
import { defaultNodeDimensions } from 'components/canvas/components/nodes';
import { PROTOCOL_PADDING } from 'components/canvas/components/nodes/components/protocol-node';
import { AuthorizationActions } from 'routes/surface/hooks/use-authorization';
import { filterOutNodesInPlaceholder } from 'utils';

type BoundingBox = [number, number][];

type SelectedNode = SurfaceNodeUnion & {
  absolutePosition: Position;
  boundingBox: BoundingBox;
};

export type SelectedNodes = SelectedNode[];

export default function useSelectedNodes(surface: string): SelectedNodes {
  const [selectedArea] = useSelectedArea();

  const fetchSurfaceNodesFromCache = useFetchSurfaceNodesFromCache(surface);

  const { findShapeById } = useCanvas();

  const nodes = fetchSurfaceNodesFromCache() || [];

  const authorization = useCurrentSurfacePermissions();

  if (selectedArea) {
    const selectedNodes = nodes
      .filter(isUserAllowedToOperateOnNode(authorization))
      .map((node) => isInsideBox(findShapeById)(node, selectedArea))
      .filter(Boolean)
      .map((node) => {
        const nodeShape = findShapeById(node.id);

        return {
          ...node,
          absolutePosition: nodeShape?.getAbsolutePosition(),
        };
      });

    return filterOutNodesInPlaceholder<SelectedNode>(selectedNodes);
  }

  return [];
}

function isUserAllowedToOperateOnNode(authorization: AuthorizationActions) {
  return function (node: SurfaceNodeUnion) {
    const canDelete =
      authorization.canDeleteSurfaceNode() ||
      authorization.canDeleteOwnedSurfaceNode(node);

    const canReposition =
      authorization.canRepositionSurfaceNode() ||
      authorization.canRepositionSurfaceNode(node);

    const canCopy = authorization.canCopySurfaceNode();

    // This is just for readability
    const canCut = canDelete;

    return canDelete || canReposition || canCopy || canCut;
  };
}

function isInsideBox(findShapeById) {
  return function (value, box, greedy = false) {
    const nodeShape = findShapeById(value.id);

    const node = {
      ...nodeShape?.position(),
      ...getNodeDimensions(value),
    };

    const points = [
      [node.x, node.y],
      [node.x + node.width, node.y],
      [node.x + node.width, node.y + node.height],
      [node.x, node.y + node.height],
    ];

    const selectedArea = [
      [box.x, box.y],
      [box.x + box.width, box.y],
      [box.x + box.width, box.y + box.height],
      [box.x, box.y + box.height],
    ];

    const matches = points
      .map((point) => pointInPolygon(point, selectedArea))
      .filter(Boolean);

    if (greedy) {
      return matches.length > 0 ? { ...value, boundingBox: points } : false;
    } else {
      return matches.length === 4 ? { ...value, boundingBox: points } : false;
    }
  };
}

function getNodeDimensions(node: SurfaceNodeUnion): Dimension {
  // @ts-ignore
  if (node.type === SurfaceNodeType.Protocol && node?.contains) {
    const dimensions =
      // @ts-ignore
      defaultNodeDimensions[node.contains.type?.toLowerCase()] ??
      defaultNodeDimensions.general;

    return {
      width: dimensions.width + PROTOCOL_PADDING * 2,
      height: dimensions.height + PROTOCOL_PADDING * 2,
    };
  } else {
    return (
      defaultNodeDimensions[node.type?.toLowerCase()] ??
      defaultNodeDimensions.general
    );
  }
}
